import { DestroyRef, inject } from '@angular/core';
import { Auth, User as FirebaseUser, onAuthStateChanged } from '@angular/fire/auth';
import { AdminClaimService } from '@core/services/admin-claim.service';
import { AnalyticsService } from '@core/services/analytics';
import { ConsentService } from '@core/services/consent';
import { Store } from '@ngxs/store';
import { ClearUser, SetPlatformAdmin, SetUser } from '@states/auth/auth.actions';
import {
  ClearOrganizationState,
  LoadOrganization,
  LoadUserOrganizations,
  SetActiveOrganization,
} from '@states/organization/organization.actions';
import { OrganizationState } from '@states/organization/organization.state';
import { Subscription, firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserProfile, UserProfileService } from '../services/user-profile.service';

/**
 * Auth initializer - waits for Firebase auth state to load before app starts
 * This ensures auth state is available synchronously in guards/resolvers
 *
 * Returns a promise that resolves when Firebase auth has initialized
 */
export function initializeAuth() {
  const auth = inject(Auth);
  const store = inject(Store);
  const userProfileService = inject(UserProfileService);
  const analyticsService = inject(AnalyticsService);
  const adminClaims = inject(AdminClaimService);
  const consentService = inject(ConsentService);
  const destroyRef = inject(DestroyRef);

  // Keep a live subscription to the user's profile so role/permission changes propagate instantly
  let profileSubscription: Subscription | undefined;
  let hasResolved = false;

  const mapProfileToUser = (userProfile: UserProfile, firebaseUser: FirebaseUser) => ({
    id: userProfile.uid,
    email: userProfile.email,
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    avatar: userProfile.photoURL,
    phone: userProfile.phone,
    phoneVerified: userProfile.phoneVerified || false,
    location: userProfile.location,
    bio: userProfile.bio,
    dateOfBirth: userProfile.dateOfBirth,
    role: userProfile.role ?? 'user',
    organizationId: userProfile.organizationId, // Load organizationId from user document
    verifiedHost: userProfile.verifiedHost,
    verifiedAt: userProfile.verifiedAt,
    verifiedBy: userProfile.verifiedBy,
    notificationPreferences: userProfile.notificationPreferences,
    createdAt: userProfile.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updatedAt: userProfile.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    isVerified: firebaseUser.emailVerified,
    rating: 0,
    reviewCount: 0,
    // Stripe (consolidated nested structure)
    stripe: userProfile.stripe,
  });

  const mapBasicUser = (firebaseUser: FirebaseUser) => {
    const nameParts = firebaseUser.displayName?.split(' ') || [];
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      avatar: firebaseUser.photoURL || undefined,
      role: 'user' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isVerified: firebaseUser.emailVerified,
      rating: 0,
      reviewCount: 0,
    };
  };

  return () =>
    new Promise<void>((resolve) => {
      // Set up persistent listener for auth + profile changes.
      // The Firebase Auth instance outlives the Angular app on the server
      // (one shared instance, one app per request), so the listener must be
      // removed when the app is destroyed. Without this every server render
      // stayed in memory and the SSR instance ran out of memory.
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        // Clean up any previous profile listener when auth state changes
        if (profileSubscription) {
          profileSubscription.unsubscribe();
          profileSubscription = undefined;
        }

        if (user) {
          // User is signed in - load profile and set state
          try {
            const userProfile = await userProfileService.getUserProfile(user.uid);

            // Platform admin comes from the ID-token claim, not the profile
            // (HMM-283). onAuthStateChanged also fires on token refresh, so a
            // revoked claim drops out of state within the hour. If the profile
            // says admin but the cached token does not, refresh once — the
            // claim may have been granted since the token was minted.
            let isPlatformAdmin = await adminClaims.hasAdminClaim();
            if (!isPlatformAdmin && userProfile?.role === 'admin') {
              isPlatformAdmin = await adminClaims.hasAdminClaim(true);
            }
            store.dispatch(new SetPlatformAdmin(isPlatformAdmin));

            if (userProfile) {
              // Wait for user state to be set before resolving
              await firstValueFrom(
                store.dispatch(new SetUser(mapProfileToUser(userProfile, user))),
              );

              // Set analytics user properties for GA4 segmentation
              analyticsService.setUserPropertiesFromUser({
                id: user.uid,
                createdAt:
                  userProfile.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
                organizationId: userProfile.organizationId,
                stripe: userProfile.stripe,
                isAdmin: isPlatformAdmin,
              });

              // Link any pre-login consent to this user for audit trail
              consentService.linkConsentToUser(user.uid);

              // Load user's organizations (checks active membership in subcollection)
              store.dispatch(new LoadUserOrganizations(user.uid));

              // If user owns an organization, load it and set as active ON FIRST
              // SIGN-IN ONLY. `onAuthStateChanged` re-fires on Firebase token
              // refresh (~hourly), and stomping the active org on each fire was
              // the root cause of the 2026-05-16 Strafford→Moray mis-attribution:
              // an admin who had switched into another host's org via the picker
              // would get silently reset back to their own org on the next token
              // refresh, with no UI signal.
              if (userProfile.organizationId) {
                store.dispatch(new LoadOrganization(userProfile.organizationId));
                const currentActiveOrgId = store.selectSnapshot(
                  OrganizationState.activeOrganizationId,
                );
                if (!currentActiveOrgId) {
                  store.dispatch(new SetActiveOrganization(userProfile.organizationId));
                }
              }
            } else {
              // No profile - set basic auth data and wait
              await firstValueFrom(store.dispatch(new SetUser(mapBasicUser(user))));
            }

            // Listen for live profile updates (role changes, verification, etc.)
            profileSubscription = userProfileService
              .getUserProfileObservable(user.uid)
              .pipe(
                catchError((error) => {
                  // Ignore permission issues for new users; avoid breaking auth flow
                  if (error?.code !== 'permission-denied') {
                    console.error('Error listening for profile updates:', error);
                  }
                  return of(undefined);
                }),
              )
              .subscribe((profile) => {
                if (!profile) return;
                store.dispatch(new SetUser(mapProfileToUser(profile, user)));
              });
          } catch (error) {
            console.error('Error loading user profile during init:', error);
            // Fall back to basic Firebase user data so initialized is always set
            await firstValueFrom(store.dispatch(new SetUser(mapBasicUser(user))));
          }
        } else {
          // User is signed out — clear org state too so a subsequent sign-in
          // (potentially as a different user) doesn't inherit the previous
          // session's activeOrganizationId via the first-sign-in-only guard
          // below.
          store.dispatch(new ClearOrganizationState());
          await firstValueFrom(store.dispatch(new ClearUser()));
        }

        // Resolve after the first auth state so the app can bootstrap
        if (!hasResolved) {
          hasResolved = true;
          resolve();
        }
      });

      destroyRef.onDestroy(() => {
        unsubscribe();
        profileSubscription?.unsubscribe();
      });
    });
}
